import Axios from 'axios'
import humps from 'humps'
import { paths } from 'routes/paths'

import { getCookie, getJWT } from '../helpers/cookies.helper'

const refreshToken = getCookie(process.env.REACT_APP_REFRESH_COOKIE as string)
let retry = false
let requestQueue = [] as any

const onAccessTokenFetched = (token: string | null) => {
  requestQueue.forEach((callback: any) => callback(token))
  requestQueue = []
}

const addToQueue = (callback: any) => {
  requestQueue.push(callback)
}

const Api = Axios.create({
  baseURL: `//${process.env.REACT_APP_API_URL}`,
})

Api.interceptors.request.use(
  (config) => {
    config.headers.Authorization = `Bearer ${getJWT()}` || null
    if (!(config.data instanceof FormData)) {
      config.data = humps.decamelizeKeys(config.data)
    }
    // const { language } = I18n
    // config.params = { locale: language, ...config.params }
    return config
  },
  (error) => Promise.reject(error)
)

Api.interceptors.response.use(
  (response) => {
    if (response.config.responseType !== 'blob') {
      response.data = humps.camelizeKeys(response.data)
    }

    return response
  },
  async (error) => {
    return errorInterceptor(error)
  }
)

const getRefreshToken = async () => {
  const baseUrl = process.env.REACT_APP_MASTER_API
  if (!baseUrl) {
    return { jwt: '', refresh_token: '' }
  }
  return Axios.post(
    `${baseUrl}/auth/refresh_token`,
    { refresh_token: refreshToken },
    { headers: { jwt: getJWT() } }
  )
    .then(({ data: { jwt, refresh_token } }: any) => {
      return { jwt, refresh_token }
    })
    .catch((err) => {
      console.error(err)
      return { jwt: '', refresh_token: '' }
    })
}

const errorInterceptor = async (error: any) => {
  const originalRequest = error.config

  // Sin respuesta HTTP (red caída, CORS, timeout): no acceder a error.response
  if (!error.response) {
    return Promise.reject(error)
  }

  if (error.response.status === 403) {
    const responseData = humps.camelizeKeys(error.response.data || {}) as {
      reason?: string
    }
    error.response.data = responseData

    // Evitar redirección global para endpoints específicos que pueden fallar silenciosamente
    const ignoredForbiddenUrls = [
      'load_percentage',
      'tag_ranking',
      'block_session/block_instances',
    ]
    const isIgnoredUrl = ignoredForbiddenUrls.some((url) =>
      originalRequest.url.includes(url)
    )

    const isNormativeAccessBlocked =
      responseData.reason === 'missing_attestation' ||
      responseData.reason === 'incomplete_profile'

    if (!isIgnoredUrl && !isNormativeAccessBlocked) {
      window.location.href = paths.forbidden
    }
    return Promise.reject(error)
  }

  if (error.response.status === 419) {
    if (originalRequest._retry) {
      return Promise.reject(error)
    }
    originalRequest._retry = true

    const needRetry = new Promise((resolve, reject) => {
      addToQueue((accessToken: string | null) => {
        if (!accessToken) {
          reject(error)
          return
        }
        try {
          originalRequest.headers.Authorization = 'Bearer ' + accessToken
          resolve(Api(originalRequest))
        } catch (err) {
          console.error(err)
          reject(err)
        }
      })
    })

    if (!retry) {
      retry = true
      try {
        const accessToken = await getRefreshToken()
        retry = false

        if (!accessToken?.jwt) {
          onAccessTokenFetched(null)
          return needRetry
        }

        document.cookie = `${process.env.REACT_APP_JWT_COOKIE as string}=${
          accessToken.jwt
        }; Path=/; Domain=${process.env.REACT_APP_COOKIE_DOMAIN}`
        document.cookie = `${process.env.REACT_APP_REFRESH_COOKIE as string}=${
          accessToken.refresh_token
        }; Path=/; Domain=${process.env.REACT_APP_COOKIE_DOMAIN}`

        Api.defaults.headers.Authorization = 'Bearer ' + accessToken.jwt
        onAccessTokenFetched(accessToken.jwt)
      } catch (refreshError) {
        console.error(refreshError)
        retry = false
        onAccessTokenFetched(null)
      }
    }
    return needRetry
  }

  error.data = humps.camelizeKeys(error.data)
  return Promise.reject(error)
}

export const LinksApi = Axios.create({
  baseURL: process.env.REACT_APP_LINKS_API,
})

LinksApi.interceptors.request.use(
  (config) => {
    config.headers.Authorization = `Bearer ${getJWT()}` || null
    return config
  },
  (error) => Promise.reject(error)
)

LinksApi.interceptors.response.use(
  (response) => {
    response.data = humps.camelizeKeys(response.data)
    return response
  },
  async (error) => {
    return errorInterceptor(error)
  }
)

export default Api
